import { BreakpointObserver } from "@angular/cdk/layout";
import { AsyncPipe, DOCUMENT, NgTemplateOutlet } from "@angular/common";
import { AfterViewInit, Component, ElementRef, inject, ViewChild } from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from "@angular/material/sidenav";
import { NavigationEnd, Router } from "@angular/router";

import { combineLatest, Observable, of, Subscription, timer } from "rxjs";
import { filter } from "rxjs/operators";
import { distinctUntilChanged, map } from "rxjs/operators";

import { FeatureFlagService } from "@e-forsyning/features/feature-flag";

import { AktuelBrugerObserver, Bruger } from "../bruger";
import { UmbracoIndstillinger, UmbracoIndstillingerObserver } from "../umbraco-indstillinger";
import { APP_SHELL_LAYOUT_OPTIONS, AppShellLayoutOptions } from "./app-shell-layout.token";
import { AppShellScrollService } from "./app-shell-scroll.service";
import { AppShellSettings } from "./app-shell-settings.model";
import { DffedbDriftsstatusKnapComponent } from "../../../../web-components/src/lib/dffedb-driftsstatus-knap/dffedb-driftsstatus-knap.component";
import { AppRatingBannerComponent } from "../app-rating";
import { UlaesteDokumenterService as UlaesteDokumenterService } from "../edh";
import { NativeBackNavigationService } from "./native-back-navigation.service";
import { EforsyningSidenavMenuComponent } from "../eforsyning-menu/eforsyning-sidenav-menu/eforsyning-sidenav-menu.component";
import { ForsyningwebSidenavMenuComponent } from "../eforsyning-menu/forsyningweb-sidenav-menu/forsyningweb-sidenav-menu.component";
import { AktuelForsyningIndstillingerObserver, ForsyningIndstillinger } from "../forsyning-indstillinger";
import { BottomNavigation } from "../service-pro/bottom-navigation/bottom-navigation";
import { ServiceProCookieConsentButtonComponent } from "../service-pro/cookie-consent";
import { FeatureHighlightKey } from "../service-pro/feature-highlight/feature-highlight.model";
import { ServiceProChromeService } from "../service-pro/service-pro-chrome.service";
import { TopNavigation } from "../service-pro/top-navigation/top-navigation";
import { isTeknikerContext } from "../util";
import { RolleBodyClassService } from "../util/rolle-body-class.service";

@Component({
    selector: "app-shell",
    templateUrl: "./app-shell.component.html",
    styleUrls: ["./app-shell.component.scss"],
    imports: [
        NgTemplateOutlet,
        MatIcon,
        DffedbDriftsstatusKnapComponent,
        MatSidenavContainer,
        MatSidenav,
        EforsyningSidenavMenuComponent,
        ForsyningwebSidenavMenuComponent,
        MatSidenavContent,
        AsyncPipe,
        BottomNavigation,
        TopNavigation,
        ServiceProCookieConsentButtonComponent,
        AppRatingBannerComponent
    ]
})
export class AppShellComponent implements AfterViewInit {
    @ViewChild("navbarMobile")
    public navbarMobile: ElementRef;

    private readonly layoutOptions: AppShellLayoutOptions = inject(APP_SHELL_LAYOUT_OPTIONS, { optional: true }) ?? {};
    public readonly chrome = inject(ServiceProChromeService);

    public settings$: Observable<AppShellSettings>;
    public indstillinger$: Observable<UmbracoIndstillinger>;
    public aktuelBruger$: Observable<Bruger>;
    public aktuelForsyningIndstillinger$: Observable<ForsyningIndstillinger>;
    public navbarMobileHeight$ = new Observable<number>();
    public scrollSubscription: Subscription;

    public scrollPositionTop = true;
    public fofBruger = false;
    /** True når vi viser login-siden – top/menu skal kun skjules her. */
    public isLoginPage = false;

    public burgerMenuBadge$: Observable<number | null>;
    public eforsyningIcon$: Observable<string>;
    public featureFlagEnabled$: Observable<boolean> = inject(FeatureFlagService).isFeatureEnabledObservable();

    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly document = inject(DOCUMENT);
    private readonly indstillingerObserver = inject(UmbracoIndstillingerObserver);
    private readonly aktuelBrugerObserver = inject(AktuelBrugerObserver);
    private readonly scrollService = inject(AppShellScrollService);
    private readonly rolleBodyClassService = inject(RolleBodyClassService);
    private readonly ulaesteDokumenterService = inject(UlaesteDokumenterService);
    private readonly aktuelForsyningIndstillingerObserver = inject(AktuelForsyningIndstillingerObserver);
    private readonly router = inject(Router);

    public bottomNavigationLinks: { label: string; icon: string; route: string; id?: string; featureHighlight?: FeatureHighlightKey }[] = [
        { label: "Min side", icon: "list", route: "/min-side", id: "min-side" },
        { label: "Søg", icon: "search", route: "/install-picker", id: "soeg" },
        { label: "Oversigtskort", icon: "map", route: "/oversigtskort", id: "oversigt", featureHighlight: "highlight_Oversigtskort" }
    ];

    constructor() {
        inject(NativeBackNavigationService).initialize();
        this.indstillinger$ = this.indstillingerObserver.valueChanges;
        this.aktuelBruger$ = this.aktuelBrugerObserver.valueChanges;
        this.aktuelForsyningIndstillinger$ = this.aktuelForsyningIndstillingerObserver.valueChanges;
        const breakpointChange$ = this.breakpointObserver.observe(["(min-width: 768px)"]);
        this.settings$ = combineLatest([this.indstillinger$, breakpointChange$]).pipe(
            map(([indstillinger, state]) => ({
                isDesktop: state.matches,
                isMobile: !state.matches,
                visDriftsstatus: indstillinger.visDriftsstatus,
                driftsstatusForstyrrelserTekst: indstillinger.driftsstatusForstyrrelserTekst,
                driftsstatusIngenForstyrrelserTekst: indstillinger.driftsstatusIngenForstyrrelserTekst
            }))
        );

        this.updateIsLoginPage(this.router.url);
        this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => this.updateIsLoginPage(e.urlAfterRedirects));
        this.burgerMenuBadge$ = this.useServiceProNavigation
            ? of(null)
            : this.ulaesteDokumenterService.hentAntalUlaesteDokumenter().pipe(map((antal) => (antal > 0 ? antal : null)));
        this.eforsyningIcon$ = this.aktuelBrugerObserver.valueChanges.pipe(map((b) => (b && "lock-open") || "lock"));
    }

    public get isTeknikerMode(): boolean {
        return isTeknikerContext() || this.useServiceProNavigation;
    }

    public get hideForsyningWebMenu(): boolean {
        return !!this.layoutOptions.hideForsyningWebMenu;
    }

    public get useServiceProNavigation(): boolean {
        return !!this.layoutOptions.teknikerLoginRoute;
    }
    public toggleAriaExpanded(id: HTMLElement, menuType: string): void {
        id.attributes.getNamedItem("aria-expanded").value = id.attributes.getNamedItem("aria-expanded").value === "true" ? "false" : "true";
        id.attributes.getNamedItem("aria-label").value = id.attributes.getNamedItem("aria-expanded").value === "true" ? "Luk " + menuType : "Åben " + menuType;
    }

    public ngAfterViewInit(): void {
        // Ville gerne sætte højden i html til [style.top.px]="navbarMobile.offsetHeight"
        // Det giver imidlertid fejlen: ExpressionChangedAfterItHasBeenCheckedError
        this.navbarMobileHeight$ = timer(0, 100).pipe(
            map(() => this.navbarMobile.nativeElement.offsetHeight),
            distinctUntilChanged()
        );

        this.aktuelBrugerObserver.valueChanges.subscribe((b) => (this.fofBruger = (b && b.rolle && b.rolle === 9) || false));
    }

    public skipToContent(): void {
        const mainContent = this.document.getElementById("main");
        if (!mainContent) {
            return;
        }

        const firstFocusable = this.findFirstFocusableElement(mainContent);
        if (firstFocusable) {
            firstFocusable.focus();
            return;
        }

        mainContent.setAttribute("tabindex", "-1");
        mainContent.focus();
    }

    @ViewChild(MatSidenavContent)
    public set setMatSidenavContent(value: MatSidenavContent) {
        this.scrollService.init(value);

        if (this.scrollSubscription) {
            this.scrollSubscription.unsubscribe();
        }
        if (!value) {
            return;
        }
        this.scrollSubscription = value.elementScrolled().subscribe(() => {
            const isAtTop = value.measureScrollOffset("top") < 120;
            this.scrollPositionTop = isAtTop;
        });
    }

    private updateIsLoginPage(url: string): void {
        const path = url.replace(/^#/, "").split("?")[0].replace(/^\/+/, "");
        const extras = this.layoutOptions.extraLoginRoutePaths ?? [];
        this.isLoginPage =
            extras.includes(path) ||
            path === "login" ||
            path.startsWith("login/") ||
            path === "servicepro-login" ||
            path.startsWith("servicepro-login/") ||
            path === "tekniker-login" ||
            path.startsWith("tekniker-login/") ||
            path === "logout" ||
            path === "skift-kodeord" ||
            path.startsWith("glemt-kodeord");
    }

    private findFirstFocusableElement(container: HTMLElement): HTMLElement | null {
        const selector = [
            "a[href]",
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            "[tabindex]:not([tabindex='-1'])"
        ].join(",");

        const elements = Array.from(container.querySelectorAll<HTMLElement>(selector));
        return elements.find((el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true") ?? null;
    }
}
